import React from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  DeviceMobile,
  Database,
  Brain,
  WhatsappLogo,
  PhoneCall,
  ChatTeardropText,
  ChartLineUp,
  ShieldCheck,
  ArrowRight,
  SquaresFour
} from '@phosphor-icons/react';
import { services } from '../data/services';
import { getServiceColors } from './ServiceContent';

const ICONS = {
  Globe,
  DeviceMobile,
  Database,
  Brain,
  WhatsappLogo,
  PhoneCall,
  ChatTeardropText,
  ChartLineUp,
  ShieldCheck
};

const Expertise = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'IT Services by Growmore IT Services',
    itemListElement: services.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title,
      url: `https://growmoreitservices.com/services/${item.slug}`,
      description: item.short
    }))
  };

  return (
    <section id="services" className="pt-6 md:pt-12 pb-6 md:pb-8 bg-white dark:bg-transparent">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="section-max-width px-4 md:px-6">
        <div className="text-center mb-8 md:mb-16 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full badge-primary mb-3 sm:mb-6">
            <SquaresFour className="w-4 h-4 sm:w-5 sm:h-5 text-brand-600 dark:text-brand-400" weight="fill" />
            <span className="text-[11px] sm:text-sm font-semibold tracking-wide uppercase">What We Do</span>
          </div>
          <h2 className="text-display text-3xl md:text-5xl font-extrabold mb-3 md:mb-5 text-gray-900 dark:text-white leading-tight">
            Our <span className="text-gradient">Services</span>
          </h2>
          <p className="text-body text-sm md:text-lg leading-relaxed">
            Website development, mobile apps, ERP, WhatsApp Business API, AI automation, IVR, bulk SMS, SEO and cyber security — built in Satara for businesses across Maharashtra and India.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-8">
          {services.map((item, index) => {
            const Icon = ICONS[item.icon] || Globe;
            const colors = getServiceColors(item.color);
            const preview = item.sections[0]?.items.slice(0, 3) || [];

            return (
              <Link
                key={item.slug}
                to={`/services/${item.slug}`}
                style={{ animationDelay: `${index * 70}ms` }}
                className="service-card group block text-left rounded-2xl sm:rounded-3xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-700 overflow-hidden shadow-soft hover:shadow-large hover:-translate-y-1 smooth-transition duration-500 animate-fade-in"
              >
                <div className="relative h-40 sm:h-48 overflow-hidden bg-slate-100 dark:bg-dark-700">
                  <img
                    src={item.image}
                    alt={`${item.title} in Satara — Growmore IT Services`}
                    className="w-full h-full object-cover object-center service-card-image"
                  />
                  <div
                    className={`absolute left-5 -bottom-6 w-14 h-14 rounded-2xl ${colors.bg} ${colors.text} flex items-center justify-center text-2xl shadow-medium border border-white/80 dark:border-white/10 group-hover:scale-110 group-hover:-rotate-3 smooth-transition`}
                  >
                    <Icon weight="duotone" />
                  </div>
                </div>

                <div className="pt-8 sm:pt-10 px-4 sm:px-6 pb-5 sm:pb-6">
                  <h3 className={`text-display text-lg sm:text-[1.35rem] leading-snug font-extrabold tracking-tight mb-2 sm:mb-3 ${colors.text}`}>
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-3 sm:mb-4 line-clamp-2">
                    {item.short}
                  </p>

                  <ul className="space-y-1.5 mb-5">
                    {preview.map((feature) => (
                      <li key={feature} className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
                        • {feature}
                      </li>
                    ))}
                  </ul>

                  <div className={`inline-flex items-center gap-2 text-sm font-bold ${colors.text}`}>
                    View details
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 smooth-transition" weight="bold" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Expertise;
